import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { enqueueSnackbar } from 'notistack'
import i18n from '../../i18n'
import { fetchEntityRecordsListBySearch } from '../../services/entityServices'
import { updateLoader } from './loaderSlice'
import {
    AttributeType,
    BUSINESS_RULES_FIELD_ID,
    EntityInstanceTypes,
    EntityStepTypes,
} from '../../constants/constants'
import {
    fetchAvalibleOrgsAndTemplates,
    fetchEntityInstanceData,
} from '../../services/entityInstanceServices'
import {
    updateAttributeValues,
    setDefaultValues,
    keepAvailableReferences,
    presetValuesToFormValues,
} from '../../utils/EntityAttributesUtils'
import { getClearedAttributeNames } from '../../utils/ClearedAttributesUtils'
import {
    getPresetScopeParams,
    isTemplatableAttribute,
    PresetSelection,
    resolvePreset,
} from '../../utils/EntityPresetUtils'
import {
    EntityPreset,
    fetchEntityPresets,
} from '../../services/entityPresetServices'
import { RootState } from '../store'
import {
    isBoolean,
    isDecimalType,
    isStringType,
} from '../../utils/AttributeUtils'
import { isNumber } from '@mui/x-data-grid/internals'

/**
 * A selectable record returned by the entity-selector API for a relation
 * step. Only the fields the relation UI actually reads are declared here —
 * the endpoint returns more.
 */
export interface RelationReferenceRecord {
    entityId: string
    templateId: string
    templateName: string
    uid: string
    displayName: string
    status?: string
    organizationId?: string
    organizationName?: string
}

export interface SelectedItemsInRelations {
    displayName: string
    entityId: string
    templateId: string
    templateName: string
    status: string
}

export interface EntityStep {
    step: string //backend sqauence //as of now i am setting frontend
    stepNo: number // frontend steps to UI
    stepType:
        | EntityStepTypes.Attributes
        | EntityStepTypes.Relations
        | EntityStepTypes.Start_step
    // orgId: string
    hasProgramStep?: boolean
    hasDeviceRelation?: boolean
    instanceId?: string //instance Id ,if it's new it is empty, only available in step 1
    templateId: string
    templateName: string
    /* Heterogeneous by design: template attributes (IAttribute2) plus the
       synthetic Business Rule Set and device-relation fields this slice
       injects, neither of which satisfies IAttribute2. Typing this properly
       means relaxing the AttributeUtils predicates first. */
    attributes?: any[]
    readOnly: boolean
    required: boolean
    status?: string //entity status only in first step
    relationName: string //for showing in stepName
    relationId: string
    relationType?: string
    mapping?: string
    referenceList?: RelationReferenceRecord[]
    selectedItems?: SelectedItemsInRelations[]
    selectedUID?: string
    deletedItems?: SelectedItemsInRelations[]
    disabled?: boolean
    orgId: string
}

export interface DeviceRelationData {
    templateId: string
    templateName: string
    readOnly: boolean
    required: boolean
    relationName: string
    relationId: string
    relationType?: string
    mapping?: string
    referenceList?: RelationReferenceRecord[]
    selectedItems?: SelectedItemsInRelations[]
    selectedUID?: string
    deletedItems?: SelectedItemsInRelations[]
    disabled?: boolean
    orgId?: string
}

export interface EntityInstancePath {
    pathName: string
    pathId: string
    activeStep: number
    orgId: string
    templateName: string
    templateId: string
    entityId: string
    entityName: string
    relationId: string
    relationName: string
    parentInstanceId: string
    parentValues: object
}

export interface StartStep {
    orgId: string
    instanceExistingOrgId: string
    parentTemplateId: string //which entity template we are creating from table
    parentTemplateName: string
    templateId: string // selcted template
    templateName: string
}
export interface AvailableOrgList {
    id: string
    name: string
    templates: SelectedOrgTemplates[]
}

export interface SelectedOrgTemplates {
    id: string
    name: string
}

interface EntityType {
    path: EntityInstancePath[]
    firstLevelSteps: EntityStep[]
    activeFirstLevelStep: number
    entitySteps: EntityStep[]
    activeEntityStep: number
    hasStartStep: boolean
    startStep: StartStep
    availableOrgList: AvailableOrgList[]
    readOnly: boolean
    deviceRelation: DeviceRelationData | null
    instanceType: EntityInstanceTypes.NORMAL | EntityInstanceTypes.IN_TRANSIT
    /** Preset templates on offer for the Program chosen in the create wizard. */
    presetsInScope: EntityPreset[]
    /** The user's choice: 'auto' applies the scope default, 'none' is Clear. */
    presetSelection: PresetSelection
    /** The preset template currently filling the form, if any. */
    appliedPresetId: string
    /** The wizard was opened from "New Template": it only authors a template, so record-creation buttons and the template picker are left out. */
    isMakingTemplate: boolean
    /** Set when the wizard was opened from a preset template's Edit: the form is that preset template's. */
    presetBeingEdited: EntityPreset | null
    /** How the template being edited was opened (its name and resolved Program), to tell whether anything changed. */
    presetEditBaseline: { name: string; orgId: string } | null
    /** The server's "name already exists" message for the template being edited; cleared when the name changes. */
    presetNameError: string
    /** Every preset template the org has for the entity type, across all scopes, for the template dropdown. */
    entityPresets: EntityPreset[]
}

const initialState: EntityType = {
    path: [], //this heart of entity instance create/update popup. based on this we are updating and showing the popup UI(entity steps). if we update this path, entity steps will update according to it.
    firstLevelSteps: [], // this is just showing in top of the popup. if have more paths(site/building/asset/device)
    activeFirstLevelStep: 0,
    entitySteps: [], //entity steps(attributes and relations) to create/update
    activeEntityStep: 0,
    hasStartStep: false,
    startStep: {
        orgId: '',
        instanceExistingOrgId: '', //this org where instance is
        parentTemplateId: '',
        parentTemplateName: '',
        templateId: '',
        templateName: '',
    },
    availableOrgList: [],
    deviceRelation: null,
    readOnly: false,
    instanceType: EntityInstanceTypes.NORMAL,
    presetsInScope: [],
    presetSelection: 'auto',
    appliedPresetId: '',
    isMakingTemplate: false,
    presetBeingEdited: null,
    presetEditBaseline: null,
    presetNameError: '',
    entityPresets: [],
}

export const setAvailableOrgList = createAsyncThunk(
    'entityInstance/setAvailableOrgList',
    async (params: { orgId: string; templateId: string }, thunkAPI) => {
        thunkAPI.dispatch(updateLoader(true))
        try {
            const { orgId, templateId } = params
            const availableOrgsList = await fetchAvalibleOrgsAndTemplates(
                orgId,
                templateId,
            )
            const updatedAvailableOrgsList = []
            let targetOrg = null

            for (const org of availableOrgsList) {
                if (org.id === orgId) {
                    targetOrg = { ...org, name: 'No Program' }
                } else {
                    updatedAvailableOrgsList.push(org)
                }
            }

            if (targetOrg) {
                updatedAvailableOrgsList.unshift(targetOrg)
            }
            return updatedAvailableOrgsList
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        } finally {
            thunkAPI.dispatch(updateLoader(false))
        }
    },
)

//this function is called only when we create only
export const setStartStep = createAsyncThunk(
    'enentityInstancetity/setStartStep',
    (
        params: {
            orgId: string
            templateId: string
            templateName: string
            entityId: string
        },
        thunkAPI,
    ) => {
        try {
            const { orgId, templateId, templateName, entityId } = params

            const updatedEntityStartSteps: EntityStep[] = [
                {
                    step: '1',
                    stepNo: 1,
                    stepType: EntityStepTypes.Attributes,
                    hasProgramStep: true,
                    attributes: [],
                    readOnly: false,
                    required: true,
                    templateId,
                    templateName,
                    relationId: '0',
                    relationName: 'Program', //this for showing in first step
                    instanceId: entityId,
                    disabled: false,
                    status: '',
                    orgId,
                },
            ]
            thunkAPI.dispatch(
                updateStartStep({
                    orgId: '',
                    instanceExistingOrgId: '',
                    parentTemplateId: templateId,
                    parentTemplateName: templateName,
                    templateId: '',
                    templateName: '',
                }),
            )
            thunkAPI.dispatch(updateActiveEntityStep(0))
            return updatedEntityStartSteps
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        }
    },
)

const getEntityData = createAsyncThunk(
    'entityInstance/getEntityData',
    async (
        params: {
            orgId: string
            templateId: string
            entityId: string
            hasFirstLevelEntityData: boolean
            hasProgramStep: boolean
            resetAttributes: boolean
        },
        thunkAPI,
    ) => {
        try {
            const state = thunkAPI.getState() as RootState
            const {
                orgId,
                templateId,
                entityId,
                hasFirstLevelEntityData,
                hasProgramStep,
                resetAttributes,
            } = params
            const instanceType = state?.entityInstance?.instanceType
            const isEdit = !!entityId && entityId !== '0'
            const EntityInstanceResponse = await fetchEntityInstanceData(
                orgId,
                templateId,
                entityId,
            )
            /* Preset templates only exist for a new, first-level entity. An org
               in no program has no Program step but still has its own
               (organisation) scope of preset templates. */
            let preset: EntityPreset | null = null
            if (!isEdit && hasFirstLevelEntityData) {
                const loggedInOrgId = state.org.org?.id ?? ''
                const presetsInScope = await fetchEntityPresets(
                    {
                        orgId: loggedInOrgId,
                        entityTemplateId: templateId,
                        ...getPresetScopeParams(orgId, loggedInOrgId),
                    },
                    { silent: true },
                )
                /* Editing a preset template always loads that preset template,
                   and never falls back to a scope default. */
                const editing = state.entityInstance.presetBeingEdited
                if (editing) {
                    preset =
                        presetsInScope.find((p) => p.id === editing.id) ?? null
                } else if (!state.entityInstance.isMakingTemplate) {
                    preset = resolvePreset(
                        presetsInScope,
                        state.entityInstance.presetSelection,
                        state.entityInstance.entityPresets,
                    )
                }
                thunkAPI.dispatch(
                    setPresetState({
                        presetsInScope,
                        appliedPresetId: preset?.id ?? '',
                    }),
                )
            }
            let updatedAttributesList: NonNullable<EntityStep['attributes']> =
                updateAttributeValues(
                    EntityInstanceResponse?.attributes ?? [],
                    thunkAPI,
                    isEdit,
                )
            // when changing program or templates(sub-templates). we are keeping user entried data for his conveniences. but clearing some fields
            if (resetAttributes) {
                const enteredAttributesData =
                    state.entityInstance.entitySteps?.find(
                        (i) => i.stepType === EntityStepTypes.Attributes,
                    )

                updatedAttributesList = updatedAttributesList.map((attr) => {
                    const attribute = enteredAttributesData?.attributes?.find(
                        (a) => a.id === attr.id,
                    )
                    if (!attribute) {
                        return { ...attr }
                    }

                    /* A value that came from the previous preset template is
                       not the user's own entry, so it doesn't carry over. This
                       is what lets Clear restore the form to what it is today. */
                    if (attribute.fromTemplate) {
                        return { ...attr }
                    }

                    /* Business rules are loaded per org and product, so they
                       are cleared. Entity references (receiver, product,
                       locations) are kept and checked against the new org
                       below. Simple scalars keep the freshly fetched value;
                       everything else keeps what the user already entered. */
                    const dataType = attr?.dataType.toLowerCase()
                    const isScopedReference = dataType === AttributeType.RULESET
                    const isScalar =
                        isBoolean(attr) ||
                        isStringType(attr) ||
                        isNumber(attr) ||
                        isDecimalType(attr)

                    let value = attribute?.value
                    if (isScopedReference) {
                        value = null
                    } else if (isScalar) {
                        value = attr?.value
                    }

                    return { ...attr, value }
                })
            }
            /* What the user already chose that must still be valid under the new
               Program: references that are not shared into it are cleared, and
               the user is told which. */
            let keptProductRulesets: { id: string; name: string }[] = []
            const clearedAttributeIds: string[] = []
            if (resetAttributes && !isEdit) {
                const kept = await keepAvailableReferences(
                    updatedAttributesList,
                    orgId,
                )
                updatedAttributesList = kept.attributes
                keptProductRulesets = kept.productRulesets
                clearedAttributeIds.push(...kept.unavailableAttributeIds)
            }
            //selecting default values from organization preferences (product, shipper, receiver)
            //if default product is there, we need to update respective business rulesets also
            let defaultProductRuleset: { id: string; name: string }[] = []
            if (!isEdit || resetAttributes) {
                /* An applied preset template is the only source of defaults;
                   the org's legacy defaults run only when none is applied. */
                const allPresetFormValues = preset
                    ? presetValuesToFormValues(
                          EntityInstanceResponse?.attributes ?? [],
                          preset.attributeValues ?? {},
                          thunkAPI,
                          isTemplatableAttribute,
                      )
                    : undefined
                /* A field the user has edited keeps their value; the template
                   fills only the rest. */
                const editedIds = new Set(
                    resetAttributes
                        ? (
                              state.entityInstance.entitySteps?.find(
                                  (i) =>
                                      i.stepType === EntityStepTypes.Attributes,
                              )?.attributes ?? []
                          )
                              .filter((a) => a.userEdited)
                              .map((a) => a.id)
                        : [],
                )
                const presetFormValues = allPresetFormValues
                    ? Object.fromEntries(
                          Object.entries(allPresetFormValues).filter(
                              ([id]) => !editedIds.has(id),
                          ),
                      )
                    : undefined
                const {
                    updatedAttributes,
                    productRulesets,
                    unavailableAttributeIds,
                } = await setDefaultValues(
                    updatedAttributesList,
                    orgId,
                    presetFormValues,
                )
                clearedAttributeIds.push(...(unavailableAttributeIds ?? []))
                updatedAttributesList = presetFormValues
                    ? updatedAttributes.map((attr) =>
                          Object.prototype.hasOwnProperty.call(
                              presetFormValues,
                              attr.id,
                          ) &&
                          attr.value !== null &&
                          attr.value !== undefined &&
                          attr.value !== ''
                              ? { ...attr, fromTemplate: true }
                              : attr,
                      )
                    : updatedAttributes
                defaultProductRuleset = productRulesets.length
                    ? productRulesets
                    : keptProductRulesets
            }
            const clearedNames = getClearedAttributeNames(
                updatedAttributesList,
                clearedAttributeIds,
                state.entityInstance.entitySteps?.find(
                    (i) => i.stepType === EntityStepTypes.Attributes,
                )?.attributes ?? [],
            )
            if (clearedNames.length > 0) {
                enqueueSnackbar(
                    i18n.t('entityPreset.valuesCleared', {
                        names: clearedNames.join(', '),
                        count: clearedNames.length,
                    }),
                    { variant: 'warning' },
                )
            }
            if (EntityInstanceResponse?.hasBusinessRulesets) {
                const businessRulesField = {
                    id: BUSINESS_RULES_FIELD_ID,
                    name: 'Business Rule Set',
                    dataType: BUSINESS_RULES_FIELD_ID,
                    value:
                        isEdit && !resetAttributes
                            ? EntityInstanceResponse?.ruleSetData
                            : Array.from(
                                  new Map(
                                      [
                                          ...(EntityInstanceResponse?.defaultRulesets ??
                                              []),
                                          ...(defaultProductRuleset ?? []),
                                      ].map((item) => [item.id, item]),
                                  ).values(),
                              ),
                    defaultRulesets:
                        EntityInstanceResponse?.defaultRulesets || [],
                    templateId,
                }

                updatedAttributesList = [
                    ...updatedAttributesList,
                    businessRulesField,
                ]
            }

            //if shipment has device relation. we are adding that in as attribute for relation selection.
            const deviceRelation = EntityInstanceResponse?.relations?.find(
                (i) => i.relationId === 'device',
            )
            if (
                instanceType.toLocaleLowerCase() ===
                    EntityInstanceTypes.IN_TRANSIT &&
                !!deviceRelation
            ) {
                /**
                 * Disable the Trip (device relation) only while editing a
                 * shipment that already has a device attached — the device
                 * can't be swapped mid-trip. When no device is set (e.g. the
                 * shipment was created from the Shipment entity workspace where
                 * Trip is optional), keep the field enabled so the mandatory
                 * Trip can be selected; leaving it disabled would also skip the
                 * required-field validation on save (UBQ-5377).
                 */
                const hasSelectedDevice =
                    (deviceRelation?.selectedEntities?.length ?? 0) > 0
                const deviceRelationAttribute = {
                    ...deviceRelation,
                    id: deviceRelation?.relationId,
                    dataType: AttributeType.DEVICE_RELATION,
                    required: true,
                    disabled: isEdit && hasSelectedDevice,
                    parentTemplateId: templateId,
                    value: deviceRelation?.selectedEntities?.map((i) => {
                        return { ...i, displayName: i.uid }
                    }),
                }

                updatedAttributesList = [
                    deviceRelationAttribute,
                    ...updatedAttributesList,
                ]
            }

            let updatedEntitySteps: EntityStep[] = []
            const updatedEntityFirstSteps: EntityStep = {
                step: '1',
                stepNo: 1,
                stepType: EntityStepTypes.Attributes,
                hasProgramStep,
                attributes: updatedAttributesList || [],
                readOnly: false,
                required: true,
                templateId,
                templateName: EntityInstanceResponse?.templateName,
                relationId: '0',
                relationName: EntityInstanceResponse?.templateName, //this for showing in first step
                instanceId: entityId, //this should here. updating things based on this
                disabled: false,
                status: EntityInstanceResponse?.status || '',
                orgId,
            }
            updatedEntitySteps = [updatedEntityFirstSteps]

            EntityInstanceResponse?.relations?.forEach((item) => {
                const updatedSelectedItems = item.selectedEntities?.map((i) => {
                    return { ...i, displayName: i.uid }
                })
                const relationStepObj: EntityStep = {
                    step: (updatedEntitySteps?.length + 1).toString(),
                    stepNo: updatedEntitySteps?.length + 1,
                    stepType: EntityStepTypes.Relations,
                    templateId: item.templateId,
                    templateName: item.templateName,
                    relationId: item.relationId,
                    relationName: item.displayName,
                    relationType: item.relationType,
                    mapping: item.mapping,
                    // readOnly: item.readOnly,
                    // required: item.required,
                    readOnly: true,
                    required: false,
                    referenceList: [],
                    selectedItems: updatedSelectedItems || [],
                    selectedUID: '',
                    deletedItems: [],
                    disabled: !isEdit,
                    orgId,
                }

                if (instanceType === EntityInstanceTypes.IN_TRANSIT) {
                    if (item.relationId !== 'device') {
                        updatedEntitySteps = [
                            ...updatedEntitySteps,
                            relationStepObj,
                        ]
                    }
                } else {
                    updatedEntitySteps = [
                        ...updatedEntitySteps,
                        relationStepObj,
                    ]
                }
            })
            if (hasFirstLevelEntityData) {
                thunkAPI.dispatch(updateFirstLevelSteps(updatedEntitySteps))
            }
            return updatedEntitySteps
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        }
    },
)

export const updateEntityInstancePath = createAsyncThunk(
    'entityInstance/updateEntityInstancePath',
    async (
        payload: {
            path: EntityInstancePath[]
            isFirst: boolean
            hasStartStep: boolean
            resetAttributes?: boolean
        },
        thunkAPI,
    ) => {
        const { path, isFirst, hasStartStep, resetAttributes } = payload
        const state = thunkAPI.getState() as RootState
        thunkAPI.dispatch(updateLoader(true))
        try {
            const pathIndex = path?.length - 1
            const getEntityDataParams = {
                orgId: path[0].orgId,
                templateId: path[pathIndex].templateId,
                entityId: path[pathIndex].entityId,
                hasFirstLevelEntityData: isFirst,
                hasProgramStep:
                    hasStartStep ||
                    (path?.length === 1
                        ? state?.entityInstance?.hasStartStep
                        : false),
                resetAttributes: !!resetAttributes,
            }
            await thunkAPI.dispatch(getEntityData(getEntityDataParams))
            thunkAPI.dispatch(
                updateActiveEntityStep(path[pathIndex].activeStep || 0),
            )
            return path
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        } finally {
            thunkAPI.dispatch(updateLoader(false))
        }
    },
)

export const setRelationStepData = createAsyncThunk(
    'entityInstance/setRelationStepData',
    async (step: number, thunkAPI) => {
        const { dispatch, getState } = thunkAPI
        dispatch(updateLoader(true))
        try {
            const state = getState() as RootState
            const orgId = state.entityInstance.startStep.orgId
            const entitySteps = state.entityInstance.entitySteps
            const entityID = entitySteps[step]?.templateId
            const parentTemplateID = entitySteps?.find(
                (i) => i.stepType === EntityStepTypes.Attributes,
            )?.templateId
            const isRelationStep =
                entitySteps[step]?.stepType === EntityStepTypes.Relations
            const searchText = ''
            let updateSteps = [...entitySteps]
            if (entityID && isRelationStep) {
                const relationEntityRecords =
                    await fetchEntityRecordsListBySearch(
                        orgId,
                        entityID,
                        parentTemplateID || '',
                        searchText,
                    )

                updateSteps = updateSteps.map((s: EntityStep) => {
                    if (s.stepNo - 1 === step) {
                        return {
                            ...s,
                            referenceList: relationEntityRecords || [],
                        }
                    } else {
                        return s
                    }
                })
            }
            return updateSteps
        } catch (error) {
            return thunkAPI.rejectWithValue(error)
        } finally {
            dispatch(updateLoader(false))
        }
    },
)

const entityInstanceSlice = createSlice({
    name: 'entityInstance',
    initialState,
    reducers: {
        updateFirstLevelSteps(state, action) {
            state.firstLevelSteps = action.payload
        },
        updateActiveFirstLevelStep(state, action) {
            state.activeFirstLevelStep = action.payload
        },
        updateEntitySteps(state, action) {
            state.entitySteps = action.payload
        },
        updateActiveEntityStep(state, action) {
            state.activeEntityStep = action.payload
        },
        updatePath(state, action) {
            state.path = action.payload
        },
        updateHasStartStep(state, action) {
            state.hasStartStep = action.payload
        },
        updateStartStep(state, action) {
            state.startStep = { ...state.startStep, ...action.payload }
        },
        updateAvailableOrgList(state, action) {
            state.availableOrgList = action.payload
        },
        updateInstanceReadOnly(state, action) {
            state.readOnly = action.payload
        },
        updateDeviceRelationData(state, action) {
            state.deviceRelation = action.payload
        },
        updateEntityInstanceType(state, action) {
            state.instanceType = action.payload
        },
        setPresetSelection(state, action) {
            state.presetSelection = action.payload
        },
        setPresetState(
            state,
            action: {
                payload: {
                    presetsInScope: EntityPreset[]
                    appliedPresetId: string
                }
            },
        ) {
            state.presetsInScope = action.payload.presetsInScope
            state.appliedPresetId = action.payload.appliedPresetId
        },
        setIsMakingTemplate(state, action) {
            state.isMakingTemplate = action.payload
        },
        setPresetBeingEdited(state, action) {
            state.presetBeingEdited = action.payload
            state.presetNameError = ''
            state.presetEditBaseline = action.payload
                ? { name: action.payload.name ?? '', orgId: '' }
                : null
        },
        /* The Program the edit form resolved on opening; choosing another one later is an edit. */
        setPresetEditBaselineOrgId(state, action: { payload: string }) {
            if (state.presetEditBaseline) {
                state.presetEditBaseline.orgId = action.payload
            }
        },
        /* The name and default being edited in the edit preset template form. */
        updatePresetBeingEdited(
            state,
            action: { payload: { name?: string; isDefault?: boolean } },
        ) {
            if (state.presetBeingEdited) {
                state.presetBeingEdited = {
                    ...state.presetBeingEdited,
                    ...action.payload,
                }
                if (action.payload.name !== undefined) {
                    state.presetNameError = ''
                }
            }
        },
        setPresetNameError(state, action: { payload: string }) {
            state.presetNameError = action.payload
        },
        setEntityPresets(state, action) {
            state.entityPresets = action.payload
        },
        resetPresetState(state) {
            state.presetsInScope = []
            state.presetSelection = 'auto'
            state.appliedPresetId = ''
            state.presetBeingEdited = null
            state.presetEditBaseline = null
            state.presetNameError = ''
            state.isMakingTemplate = false
            state.entityPresets = []
        },
    },
    extraReducers: (builder) => {
        /* `action` is intentionally left un-annotated: createAsyncThunk
           already carries each thunk's return type through to `fulfilled`,
           so annotating it only widens the payload back to `any`. */
        builder.addCase(setAvailableOrgList.fulfilled, (state, action) => {
            state.availableOrgList = action.payload
        })
        builder.addCase(setAvailableOrgList.rejected, (state) => {
            state.availableOrgList = []
        })
        builder.addCase(setStartStep.fulfilled, (state, action) => {
            state.entitySteps = action.payload
        })
        builder.addCase(setStartStep.rejected, (state) => {
            state.entitySteps = []
        })
        builder.addCase(getEntityData.fulfilled, (state, action) => {
            state.entitySteps = action.payload
        })
        builder.addCase(getEntityData.rejected, (state) => {
            state.entitySteps = []
        })
        builder.addCase(updateEntityInstancePath.fulfilled, (state, action) => {
            state.path = action.payload
        })
        builder.addCase(updateEntityInstancePath.rejected, (state) => {
            state.path = []
        })
        builder.addCase(setRelationStepData.fulfilled, (state, action) => {
            state.entitySteps = action.payload
        })
        builder.addCase(setRelationStepData.rejected, (state) => {
            state.entitySteps = []
        })
    },
})

export const {
    updateFirstLevelSteps,
    updateActiveFirstLevelStep,
    updateEntitySteps,
    updatePath,
    updateHasStartStep,
    updateStartStep,
    updateActiveEntityStep,
    updateAvailableOrgList,
    updateInstanceReadOnly,
    updateDeviceRelationData,
    updateEntityInstanceType,
    setPresetSelection,
    setPresetState,
    setPresetBeingEdited,
    setPresetEditBaselineOrgId,
    setIsMakingTemplate,
    updatePresetBeingEdited,
    setPresetNameError,
    setEntityPresets,
    resetPresetState,
} = entityInstanceSlice.actions

export default entityInstanceSlice.reducer
